import React, { useState, useEffect } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/CommitReview.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CommitReview = ({ review, issueKey, projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        
        if (!window.AP) {
          console.error('AP not available');
          throw new Error('AP not initialized');
        }

        const ap = window.AP;
        const token = await ap.context.getToken();
        const response = await fetch(`/get-commit-review?projectId=${projectId}&issueId=${issueKey}`, {
          headers: {
            'Authorization': `JWT ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch analysis');
        }

        const data = await response.json();
        setAnalysis(data);
      } catch (err) {
        console.error('Error fetching analysis:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (issueKey && projectId) {
      fetchAnalysis();
    }
  }, [issueKey, projectId]);

  if (loading) return (
    <div className="loading-message">
      Code has been committed for this issue. Analyzing the code to confirm it has met the acceptance criteria...
    </div>
  );
  if (error) return (
    <div className="error-message">
      Code review analysis is not available for this issue.
    </div>
  );
  if (!analysis) return <div className="empty-message">No analysis available</div>;

  const renderCommitLink = (commitId) => {
    const commitData = analysis.commits?.find(c => c.id.startsWith(commitId));
    
    if (!commitData?.url) {
      return <span className="commit-id">{commitId.substring(0, 7)}</span>;
    }

    return (
      <a 
        href={commitData.url}
        target="_blank"
        rel="noopener noreferrer"
        className="commit-link"
      >
        View changes ({commitId.substring(0, 7)})
      </a>
    );
  };

  return (
    <div className="commit-review">
      <div className="summary-section">
        <div className="summary-header">
          <div>
            <h2>Implementation Review</h2>
            <p className="summary-subtitle">
              Match Score: {Math.round(analysis.summary.matchScore * 100)}%
            </p>
          </div>
          <div className="match-score" style={{ backgroundColor: getStatusColor(analysis.summary.status) }}>
            {analysis.summary.status}
          </div>
        </div>
        <p className="summary-recommendation">{analysis.summary.recommendation}</p>
      </div>

      <div className="criteria-section">
        <h3>Acceptance Criteria</h3>
        {analysis.acceptanceCriteria.map((criterion) => (
          <div key={criterion.id} className="criterion-card">
            <div className="criterion-header">
              <h4>{criterion.description}</h4>
              <span className="status-badge" style={{ backgroundColor: getStatusColor(criterion.status) }}>
                {criterion.status}
              </span>
            </div>
            <p className="criterion-commentary">{criterion.commentary}</p>
            <div className="confidence-bar">
              <div 
                className="confidence-fill"
                style={{ 
                  width: `${Math.round(criterion.confidence * 100)}%`,
                  backgroundColor: getStatusColor(criterion.status)
                }}
              />
              <span>Confidence: {Math.round(criterion.confidence * 100)}%</span>
            </div>
            {criterion.matchedCommits.map((commitId) => (
              <div key={commitId} className="commit-item">
                {renderCommitLink(commitId)}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="recommendations-section">
        <h3>Recommendations</h3>
        {(analysis?.recommendations || []).map((rec, index) => (
          <div key={index} className="recommendation-card">
            <div className="recommendation-type" style={{ backgroundColor: getRecommendationColor(rec.type) }}>
              {getRecommendationIcon(rec.type)} {formatRecommendationType(rec.type)}
            </div>
            <p className="recommendation-description">{rec.description}</p>
            <div className="action-steps">
              <span className="action-label">Next Steps:</span>
              <p>{rec.suggestedAction}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper functions for colors and icons
const getStatusColor = (status) => {
  switch (status) {
    case 'MET': return '#36B37E';
    case 'PARTIAL': return '#FFAB00';
    case 'NOT_MET': return '#FF5630';
    default: return '#6B778C';
  }
};

const getRecommendationColor = (type) => {
  switch (type) {
    case 'NEEDS_ERROR_HANDLING': return '#FFF0B3';
    default: return '#F4F5F7';
  }
};

const getRecommendationIcon = (type) => {
  switch (type) {
    case 'NEEDS_ERROR_HANDLING': return '⚠️';
    default: return '❗';
  }
};

const formatRecommendationType = (type) => {
  return type.split('_').map(word => 
    word.charAt(0) + word.slice(1).toLowerCase()
  ).join(' ');
};

const styles = {
  container: {
    padding: '20px',
  },
  summarySection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  scoreCard: {
    textAlign: 'center',
    padding: '24px',
    backgroundColor: '#F4F5F7',
    borderRadius: '8px',
    width: '300px',
  },
  scoreCircle: {
    width: '120px',
    margin: '0 auto 16px',
  },
  scoreTitle: {
    fontSize: '16px',
    color: '#172B4D',
    margin: '16px 0',
  },
  recommendation: {
    fontSize: '14px',
    color: '#42526E',
    lineHeight: '1.4',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '20px',
    color: '#172B4D',
    marginBottom: '16px',
  },
  acGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  acCard: {
    backgroundColor: 'white',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    padding: '16px',
  },
  acHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  acId: {
    fontWeight: '500',
    color: '#172B4D',
  },
  badge: {
    padding: '4px 8px',
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: '500',
  },
  acDescription: {
    fontSize: '14px',
    color: '#172B4D',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  matchedCommits: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
  commitHash: {
    backgroundColor: '#EAE6FF',
    color: '#403294',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  confidenceBar: {
    height: '4px',
    backgroundColor: '#DFE1E6',
    borderRadius: '2px',
    marginTop: '12px',
    position: 'relative',
  },
  confidenceFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  confidenceLabel: {
    position: 'absolute',
    right: '0',
    top: '-20px',
    fontSize: '12px',
    color: '#42526E',
  },
  commitCard: {
    backgroundColor: 'white',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    padding: '16px',
    marginBottom: '16px',
  },
  commitHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  commitSummary: {
    fontSize: '14px',
    color: '#172B4D',
    marginBottom: '12px',
  },
  matchedACs: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  acBadge: {
    backgroundColor: '#DEEBFF',
    color: '#0747A6',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '12px',
  },
  recommendationCard: {
    backgroundColor: 'white',
    border: '1px solid #DFE1E6',
    borderRadius: '3px',
    padding: '16px',
    marginBottom: '16px',
  },
  recHeader: {
    marginBottom: '12px',
  },
  recType: {
    backgroundColor: '#EAE6FF',
    color: '#403294',
    padding: '4px 8px',
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: '500',
  },
  recDescription: {
    fontSize: '14px',
    color: '#172B4D',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  suggestedAction: {
    backgroundColor: '#F4F5F7',
    padding: '12px',
    borderRadius: '3px',
  },
  actionLabel: {
    fontSize: '12px',
    color: '#42526E',
    fontWeight: '500',
    display: 'block',
    marginBottom: '4px',
  },
  metadata: {
    fontSize: '12px',
    color: '#6B778C',
    textAlign: 'right',
    marginTop: '24px',
  },
  historySection: {
    marginTop: '32px',
  },
  actionItems: {
    marginTop: '32px',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  acImprovements: {
    marginTop: '32px',
  },
  acSuggestion: {
    marginBottom: '8px',
  },
  commitDetails: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#F4F5F7',
    borderRadius: '3px',
  },
  diffView: {
    backgroundColor: '#FFFFFF',
    padding: '12px',
    borderRadius: '3px',
    overflowX: 'auto',
    marginBottom: '12px',
  },
  commitMetadata: {
    fontSize: '12px',
    color: '#42526E',
  }
};

export default CommitReview; 